import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, MapPin, RefreshCw, Save, SlidersHorizontal, Sparkles, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Thumb, Spinner } from '@/components/ui';
import api from '@/lib/api';
import { FIT_SLOTS } from '@/lib/constants';
import type { BuildFitResponse, BuildFitOwnedCandidate } from '@/lib/types';

interface Weather {
  tempF: number;
  feelsF: number;
  condition: string;
  hi: number;
  lo: number;
  precip: number;
}

// Short WMO weather-code → label map (Open-Meteo).
const WMO: Record<number, string> = {
  0: 'clear', 1: 'mainly clear', 2: 'partly cloudy', 3: 'overcast', 45: 'fog', 48: 'fog',
  51: 'light drizzle', 53: 'drizzle', 55: 'heavy drizzle', 61: 'light rain', 63: 'rain', 65: 'heavy rain',
  66: 'freezing rain', 67: 'freezing rain', 71: 'light snow', 73: 'snow', 75: 'heavy snow', 77: 'snow',
  80: 'rain showers', 81: 'rain showers', 82: 'heavy showers', 85: 'snow showers', 86: 'snow showers',
  95: 'thunderstorms', 96: 'thunderstorms', 99: 'thunderstorms',
};

const slotLabel = (slot: string) => FIT_SLOTS.find((s) => s.id === slot)?.label ?? slot;

async function fetchWeather(lat: number, lon: number): Promise<Weather | null> {
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,apparent_temperature,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&temperature_unit=fahrenheit&timezone=auto&forecast_days=1`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const j = await r.json();
    const c = j.current ?? {};
    const d = j.daily ?? {};
    return {
      tempF: Math.round(c.temperature_2m),
      feelsF: Math.round(c.apparent_temperature),
      condition: WMO[c.weather_code as number] ?? 'mixed conditions',
      hi: Math.round((d.temperature_2m_max ?? [])[0]),
      lo: Math.round((d.temperature_2m_min ?? [])[0]),
      precip: (d.precipitation_probability_max ?? [])[0] ?? 0,
    };
  } catch {
    return null;
  }
}

async function fetchPlace(lat: number, lon: number): Promise<string | null> {
  try {
    const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`);
    if (!r.ok) return null;
    const j = await r.json();
    return j.city || j.locality || j.principalSubdivision || null;
  } catch {
    return null;
  }
}

export function WhatToWearPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<'locating' | 'building' | 'done' | 'error'>('locating');
  const [error, setError] = useState<string | null>(null);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [place, setPlace] = useState<string | null>(null);
  const [dayName, setDayName] = useState('');
  const [weekend, setWeekend] = useState(false);
  const [context, setContext] = useState('');
  const [result, setResult] = useState<BuildFitResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const ran = useRef(false);

  const build = async (w: Weather | null, city: string | null) => {
    setStage('building');
    setWeather(w);
    setPlace(city);
    const now = new Date();
    const day = now.toLocaleDateString('en-US', { weekday: 'long' });
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    setDayName(day);
    setWeekend(isWeekend);
    const parts = [`Pick today's outfit. It's ${day}, ${isWeekend ? 'a weekend (no work)' : 'a work day'}.`];
    if (w) {
      parts.push(`Weather${city ? ` in ${city}` : ''}: ${w.tempF}°F (feels ${w.feelsF}°F), ${w.condition}, high ${w.hi}° / low ${w.lo}°, ${w.precip}% precip.`);
    }
    parts.push('Use only my closet. Keep it practical and weather-appropriate, and be brief.');
    const ctx = parts.join(' ');
    setContext(ctx);
    try {
      const res = await api.post<BuildFitResponse>('/ai/build-fit', {
        context: ctx,
        seasons: [],
        fixed: [],
        disabledSlots: [],
        scopes: { otherClosets: false, wishlist: false, beyond: false },
      });
      setResult(res ?? null);
      setStage('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not put together an outfit.');
      setStage('error');
    }
  };

  const locateAndBuild = () => {
    setStage('locating');
    setError(null);
    if (!navigator.geolocation) {
      build(null, null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const [w, city] = await Promise.all([fetchWeather(latitude, longitude), fetchPlace(latitude, longitude)]);
        build(w, city);
      },
      () => {
        setError('Location is off, so I can’t check the weather. Enable location and retry, or get a suggestion without it.');
        setStage('error');
      },
      { timeout: 10000, maximumAge: 600000 },
    );
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    locateAndBuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top owned pick per slot.
  const outfit = (result?.slots ?? [])
    .map((s) => {
      const pick = s.candidates.find((c) => c.kind === 'owned') as BuildFitOwnedCandidate | undefined;
      return pick ? { slot: s.slot as string, pick } : null;
    })
    .filter((o): o is { slot: string; pick: BuildFitOwnedCandidate } => o !== null);

  const save = async (logWear: boolean) => {
    if (!outfit.length) return;
    setSaving(true);
    try {
      const pieces = outfit.map((o) => ({ itemId: o.pick.id, slot: o.slot }));
      const res = await api.post<{ fit: { id: string } }>('/fits', {
        name: `${dayName} fit`,
        seasons: [],
        pieces,
        logWear: logWear ? {} : undefined,
      });
      if (res?.fit?.id) navigate(`/fits/${res.fit.id}`);
    } finally {
      setSaving(false);
    }
  };

  const refine = async () => {
    const res = await api.post<{ session: { id: string } }>('/fit-sessions', {
      name: `Today — ${dayName}`,
      state: { context },
    });
    if (res?.session?.id) navigate(`/fits/builder?session=${res.session.id}`);
  };

  return (
    <Page>
      <PageHeader section="What to Wear Today" backHref="/fits" backLabel="Fits" />
      <Main className="space-y-5">
        {/* Context chip */}
        {(weather || dayName) && (
          <Card className="p-4 flex items-center gap-3">
            <div className="p-2.5 bg-secondary rounded-xl">
              <Sun className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {dayName}
                {dayName && <span className="text-muted-foreground font-normal"> · {weekend ? 'weekend' : 'work day'}</span>}
              </p>
              {weather ? (
                <p className="text-xs text-muted-foreground truncate">
                  {place ? `${place} · ` : ''}
                  {weather.tempF}°F (feels {weather.feelsF}°), {weather.condition} · {weather.precip}% precip
                </p>
              ) : (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Weather unavailable
                </p>
              )}
            </div>
          </Card>
        )}

        {stage === 'locating' && (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
            <Spinner className="w-6 h-6" />
            <p className="text-sm">Checking your location & weather…</p>
          </div>
        )}

        {stage === 'building' && (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
            <Sparkles className="w-6 h-6 text-primary" />
            <p className="text-sm">Putting together today’s outfit…</p>
            <p className="text-[11px]">This can take 20–40 seconds.</p>
          </div>
        )}

        {stage === 'error' && (
          <Card className="p-4 space-y-3">
            <p className="text-sm text-foreground flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              {error}
            </p>
            <div className="flex gap-2">
              <Button fullWidth onClick={locateAndBuild}>
                <RefreshCw className="w-4 h-4" /> Retry
              </Button>
              <Button fullWidth variant="secondary" onClick={() => build(null, null)}>
                Without weather
              </Button>
            </div>
          </Card>
        )}

        {stage === 'done' && result && (
          <>
            {result.recommendation?.overall && (
              <Card className="p-4 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground leading-relaxed">{result.recommendation.overall}</p>
              </Card>
            )}

            {outfit.length === 0 ? (
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">
                  Couldn’t assemble a full outfit from your closet for today. Try the Fit Builder to tweak it.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {outfit.map((o) => (
                  <div key={o.slot} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{slotLabel(o.slot)}</p>
                    <div className="aspect-square rounded-2xl overflow-hidden border border-border bg-secondary">
                      <Thumb url={o.pick.primaryPhotoUrl} alt={o.pick.name} className="w-full h-full" />
                    </div>
                    <p className="text-xs font-medium text-foreground leading-tight line-clamp-2">{o.pick.name}</p>
                    {o.pick.brand && <p className="text-[10px] text-muted-foreground">{o.pick.brand}</p>}
                  </div>
                ))}
              </div>
            )}

            {outfit.length > 0 && (
              <div className="space-y-2">
                <Button fullWidth onClick={() => save(true)} disabled={saving}>
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Wear it — save & log today'}
                </Button>
                <Button fullWidth variant="secondary" onClick={refine} disabled={saving}>
                  <SlidersHorizontal className="w-4 h-4" /> Tweak in builder
                </Button>
              </div>
            )}

            <Button fullWidth variant="ghost" onClick={locateAndBuild} disabled={saving}>
              <RefreshCw className="w-4 h-4" /> Suggest another
            </Button>
          </>
        )}
      </Main>
    </Page>
  );
}
